// Mouse sensitivity conversions.
//
// The canonical unit is the CS-equivalent value stored in settings: degrees of yaw per mouse
// count = cs * 0.022 (Source engine `m_yaw`).

/** Degrees per count at CS sensitivity 1. */
export const CS_YAW_DEG = 0.022;

/** Valorant yaw is 0.07 deg/count, so CS = Valorant * 3.181818 (0.07 / 0.022). */
export const VALORANT_RATIO = 3.181818;

/**
 * ASSUMPTION: Krunker turns 0.13751 deg per count per sensitivity unit. This is the community
 * measured constant used by converters such as FindMyMouse (Krunker 0.55 = CS 3.4375), not a value
 * published by Krunker's developers. CS = Krunker * KRUNKER_RATIO (about 6.25).
 */
export const KRUNKER_YAW_DEG = 0.13751;
export const KRUNKER_RATIO = KRUNKER_YAW_DEG / CS_YAW_DEG;

const CM_PER_INCH = 2.54;

export type SensUnit = "cs" | "valorant" | "krunker";

export const SENS_UNITS: readonly { id: SensUnit; label: string }[] = [
  { id: "cs", label: "CS2 / CSGO" },
  { id: "valorant", label: "Valorant" },
  { id: "krunker", label: "Krunker" },
];

const CS_PER_UNIT: Readonly<Record<SensUnit, number>> = {
  cs: 1,
  valorant: VALORANT_RATIO,
  krunker: KRUNKER_RATIO,
};

export function csToValorant(cs: number): number {
  return cs / VALORANT_RATIO;
}

export function valorantToCs(valorant: number): number {
  return valorant * VALORANT_RATIO;
}

export function csToKrunker(cs: number): number {
  return cs / KRUNKER_RATIO;
}

export function krunkerToCs(krunker: number): number {
  return krunker * KRUNKER_RATIO;
}

/** Convert a sensitivity between games. */
export function convertSensitivity(value: number, from: SensUnit, to: SensUnit): number {
  return (value * CS_PER_UNIT[from]) / CS_PER_UNIT[to];
}

/** Degrees of yaw per mouse count for a CS-equivalent sensitivity. */
export function degreesPerCount(cs: number): number {
  return cs * CS_YAW_DEG;
}

/** Physical mouse travel for a full turn. Returns Infinity for a non-positive sensitivity or dpi. */
export function csToCm360(cs: number, dpi: number): number {
  if (!(cs > 0) || !(dpi > 0)) return Infinity;
  const countsPer360 = 360 / degreesPerCount(cs);
  return (countsPer360 / dpi) * CM_PER_INCH;
}

/** Inverse of `csToCm360`. Returns NaN for a non-positive distance or dpi. */
export function cm360ToCs(cm: number, dpi: number): number {
  if (!(cm > 0) || !(dpi > 0)) return NaN;
  const countsPer360 = (cm / CM_PER_INCH) * dpi;
  return 360 / countsPer360 / CS_YAW_DEG;
}

/** Parse user input, accepting a decimal comma. Null when it is not a finite number. */
export function parseDecimal(text: string): number | null {
  const t = text.trim().replace(",", ".");
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** Compact display: more decimals for small values. */
export function formatSensitivity(v: number): string {
  if (!Number.isFinite(v)) return "-";
  const abs = Math.abs(v);
  const decimals = abs >= 100 ? 0 : abs >= 10 ? 1 : abs >= 1 ? 2 : 3;
  return v.toFixed(decimals);
}
